import { datadogLogs } from '@datadog/browser-logs';
import { useStableCallback } from '@main/shared/browser';
import { toError } from '@main/shared/utils';
import { useNhostClient } from '@nhost/react';
import { useTranslation } from 'react-i18next';

import { errorToast } from '../toast';

export interface UseDownloadStorageFileOptions {
  throwOnError?: boolean;
}

export function useDownloadStorageFile({
  throwOnError = false,
}: UseDownloadStorageFileOptions = {}) {
  const { t } = useTranslation('ui');
  const nhost = useNhostClient();

  return useStableCallback(async (fileId: string) => {
    const { error, presignedUrl } = await nhost.storage.getPresignedUrl({ fileId });

    if (error) {
      if (throwOnError) {
        throw error;
      }

      errorToast(t('errorMessages.fileDownloadFailed'));
      datadogLogs.logger.error('Failed downloading file', { fileId }, toError(error));
      return;
    }

    const openedWindow = window.open(presignedUrl.url, '_blank');
    if (throwOnError && !openedWindow) {
      throw new Error('Browser did not open the storage file');
    }
  });
}

export function usePublicStorageFile() {
  const nhost = useNhostClient();

  return useStableCallback((fileId?: string) =>
    fileId ? nhost.storage.getPublicUrl({ fileId }) : undefined,
  );
}
